import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import PersonalInfoStep from './components/PersonalInfoStep';
import TrainingDetailsStep from './components/TrainingDetailsStep';
import ConsentStep from './components/ConsentStep';
import SuccessModal from './components/SuccessModal';
import Logo from './assets/img/logo.png'

// Validation Schema
const validationSchema = yup.object().shape({
  isDominionMember: yup.boolean().required('Please specify if you are a Dominion City member'),
  dominionChapter: yup.string().when('isDominionMember', {
    is: true,
    then: (schema) => schema.required('Chapter name is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  personalData: yup.object().shape({
    surname: yup.string().required('Surname is required').max(50, 'Surname too long'),
    firstName: yup.string().required('First name is required').max(50, 'First name too long'),
    otherNames: yup.string().max(100, 'Other names too long'),
    gender: yup.string().required('Gender is required').oneOf(['Male', 'Female', 'Other']),
    dateOfBirth: yup.date().required('Date of birth is required'),
    // whatsappNumber: yup.string().required('WhatsApp number is required'),
    phoneNumber: yup.string().required('Phone number is required'),
    // email: yup.string().required('Email is required').email('Invalid email format'),
    residentialAddress: yup.string().required('Address is required').max(200, 'Address too long'),
    stateOfOrigin: yup.string().required('State of origin is required'),
    // lga: yup.string().required('LGA is required'),
    // occupation: yup.string().required('Occupation is required').max(100, 'Occupation too long'),
    maritalStatus: yup.string().required('Marital status is required')
  }),
  skillOfInterest: yup.string().required('Please select a skill'),
  hasPriorExperience: yup.boolean().required('Please specify your experience level'),
  availableForFullDuration: yup.boolean().required('Please confirm your availability'),
  hearAboutTraining: yup.string().required('Please tell us how you heard about the training'),
  agreeToContact: yup.boolean().oneOf([true], 'Your consent is required to submit the form'),
  // consentToMedia: yup.boolean().required('Please specify your media consent')
});

const App = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    watch,
    getValues
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      isDominionMember: false,
      dominionChapter: '',
      personalData: {
        surname: '',
        firstName: '',
        otherNames: '',
        gender: '',
        dateOfBirth: '',
        whatsappNumber: '',
        phoneNumber: '',
        email: '',
        residentialAddress: '',
        stateOfOrigin: '',
        lga: '',
        occupation: '',
        maritalStatus: ''
      },
      skillOfInterest: '',
      hasPriorExperience: false,
      availableForFullDuration: false,
      hearAboutTraining: '',
      agreeToContact: false,
      consentToMedia: false
    }
  });

  const totalSteps = 3;

  // Handle next step
  const handleNext = async () => {
    const fieldsToValidate = getFieldsForStep(currentStep);
    const isStepValid = await trigger(fieldsToValidate);

    if (isStepValid) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      setError('');
    } else {
      setError('Please fill in all required fields correctly');
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  // Get fields to validate for each step
  const getFieldsForStep = (step) => {
    switch (step) {
      case 1:
        return ['isDominionMember', 'dominionChapter', 'personalData'];
      case 2:
        return ['skillOfInterest', 'hasPriorExperience', 'availableForFullDuration', 'hearAboutTraining'];
      case 3:
        return ['agreeToContact', 'consentToMedia'];
      default:
        return [];
    }
  };

  // Handle form submission
  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      // const response = await axios.post('http://localhost:3040/api/register', data, {
      const response = await axios.post('https://okene.dominioncityph.cc/api/register', data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        setRegistrationData(response.data.data);
        setShowSuccess(true);
      }
      setLoading(false);
    } catch (error) {
      console.error('Registration failed:', error);
      setError(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate progress
  const progress = (currentStep / totalSteps) * 100;

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <PersonalInfoStep register={register} errors={errors} watch={watch} />;
      case 2:
        return <TrainingDetailsStep register={register} errors={errors} watch={watch} />;
      case 3:
        return <ConsentStep register={register} errors={errors} />;
      default:
        return <PersonalInfoStep register={register} errors={errors} watch={watch} />;
    }
  };

  return (
      <div className="min-vh-100 d-flex align-items-center py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <div className="card">
                <div className="card-body p-5">
                  {/* Logo and Branding */}
                  <div className="logo-container">
                    {/*<div className="logo">*/}
                    {/*  <i className="fas fa-graduation-cap"></i>*/}
                    {/*</div>*/}
                    <img src={Logo} alt="logo" className="img-fluid dc-logo" />
                    {/*<h1 className="brand-title">Dominion City PH</h1>*/}
                    <p className="brand-subtitle">Free Skills Acquisition Program</p>
                    <p className="text-muted">
                      Transform your future with in-demand skills every Sunday in July. Please fill the form below to register
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="progress-container">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Step {currentStep} of {totalSteps}</small>
                      <small className="text-muted">{Math.round(progress)}% Complete</small>
                    </div>
                    <div className="progress">
                      <div
                          className="progress-bar"
                          style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Step Indicators */}
                  <div className="step-indicator">
                    <div className={`step ${currentStep >= 1 ? 'completed' : 'inactive'}`}>1</div>
                    <div className={`step-line ${currentStep > 1 ? 'completed' : ''}`}></div>
                    <div className={`step ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : 'inactive'}`}>2</div>
                    <div className={`step-line ${currentStep > 2 ? 'completed' : ''}`}></div>
                    <div className={`step ${currentStep >= 3 ? 'active' : 'inactive'}`}>3</div>
                  </div>

                  {/* Error Alert */}
                  {error && (
                      <div className="alert alert-danger" role="alert">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                      </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit(onSubmit)}>
                    {renderStep()}

                    {/* Navigation Buttons */}
                    <div className="d-flex justify-content-between mt-4">
                      <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handlePrevious}
                          disabled={currentStep === 1}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        Previous
                      </button>

                      {currentStep < totalSteps ? (
                          <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleNext}
                              disabled={loading}
                          >
                            Next
                            <i className="fas fa-arrow-right ms-2"></i>
                          </button>
                      ) : (
                          <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={loading}
                          >
                            {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Submitting...
                                </>
                            ) : (
                                <>
                                  <i className="fas fa-check me-2"></i>
                                  Complete Registration
                                </>
                            )}
                          </button>
                      )}
                    </div>
                  </form>

                  {/* Footer */}
                  <div className="text-center mt-4 pt-4 border-top">
                    <p className="text-muted small mb-0">
                      © 2025 Dominion City. Raising Leaders that transform Society.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        <SuccessModal
            show={showSuccess}
            onHide={() => {
              setShowSuccess(false)
              window.location.href="/"
            }}
            registrationData={registrationData}
        />
      </div>
  );
};

export default App;